import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ActionButton, DefaultButton, PrimaryButton, Text, TextField, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import { userAPI } from '../UserAPI';

interface StripePaymentFormProps {
    customerId: string;
    onSuccess?: (paymentMethod: any) => void;
    onCancel?: () => void;
    onLoadingChange?: (loading: boolean) => void;
}

// Check if Stripe is properly configured
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey || stripePublishableKey === 'pk_test_your_key_here') {
  console.warn('⚠️ Stripe publishable key not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable with a valid Stripe test key.');
}

const stripePromise = stripePublishableKey && stripePublishableKey !== 'pk_test_your_key_here' 
  ? loadStripe(stripePublishableKey)
  : null;

const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
    hidePostalCode: true,
};

function PaymentForm({ customerId, onSuccess, onCancel, onLoadingChange }: StripePaymentFormProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    // Show configuration warning if Stripe is not properly set up
    if (!stripePublishableKey || stripePublishableKey === 'pk_test_your_key_here' || !stripe) {
        return (
            <div>
                <MessageBar messageBarType={MessageBarType.warning}>
                    <Text variant="medium" className="fontWeightBold">Stripe Configuration Required</Text>
                    <br />
                    <Text variant="small">
                        To use payment methods, you need to configure Stripe. Please:
                    </Text>
                    <br />
                    <Text variant="small">
                        1. Create a Stripe account at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">stripe.com</a>
                    </Text>
                    <br />
                    <Text variant="small">
                        2. Get your publishable key from the Stripe Dashboard
                    </Text>
                    <br />
                    <Text variant="small">
                        3. Add it to your .env file: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_your_actual_key
                    </Text>
                </MessageBar>
                <div className="marginTopM displayFlex gapM">
                    <DefaultButton onClick={onCancel} text="Cancel" />
                </div>
            </div>
        );
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            pushError(new Error('Stripe is not properly configured. Please check your environment variables.'));
            return;
        }

        setLoading(true);
        onLoadingChange?.(true);

        try {
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('Card element not found');
            }

            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name,
                    email,
                },
            });

            if (error) {
                console.error('Stripe error:', error);
                pushError(new Error(`Payment method creation failed: ${error.message}`));
                return;
            }

            // Send to your backend
            try {
                const newPaymentMethod = await userAPI.addPaymentMethod(customerId, paymentMethod.id);
                if (newPaymentMethod) {
                    onSuccess?.(newPaymentMethod);
                } else {
                    throw new Error('Server returned null/undefined for payment method');
                }
            } catch (apiError) {
                console.error('API Error details:', apiError);
                
                // Temporary solution: Show success message even if backend fails
                // This allows testing the Stripe integration while backend is being implemented
                if (apiError instanceof Error && apiError.message.includes('404')) {
                    console.log('✅ Stripe payment method created successfully!');
                    console.log('⚠️ Backend API not implemented yet - payment method not saved to server');
                    console.log('📋 Payment Method ID:', paymentMethod.id);
                    console.log('📋 Card last4:', paymentMethod.card?.last4);
                    console.log('📋 Card brand:', paymentMethod.card?.brand);
                    
                    // Show success message to user
                    onSuccess?.({
                        id: paymentMethod.id,
                        type: paymentMethod.type,
                        card: paymentMethod.card,
                        created: paymentMethod.created,
                        _temp: true // Flag to indicate this is temporary
                    });
                    return;
                }
                
                throw new Error(`Failed to save payment method to server: ${apiError instanceof Error ? apiError.message : 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Payment form error:', error);
            pushError(error instanceof Error ? error : new Error('An unexpected error occurred'));
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="marginBottomM">
                <TextField
                    label="Name auf der Karte"
                    value={name}
                    onChange={(_, newValue) => setName(newValue || '')}
                    required
                />
            </div>
            <div className="marginBottomM">
                <TextField
                    label="E-Mail"
                    type="email"
                    value={email}
                    onChange={(_, newValue) => setEmail(newValue || '')}
                    required
                />
            </div>
            <div className="marginBottomM">
                <label className="ms-Label">Kreditkartendaten</label>
                <div className="borderNeutral paddingS" style={{ minHeight: '40px' }}>
                    <CardElement options={cardElementOptions} />
                </div>
            </div>
        </form>
    );
}

export default function StripePaymentForm(props: StripePaymentFormProps) {
    // Always wrap in Elements, even if Stripe is not configured
    // The PaymentForm component will handle the configuration check internally
    return (
        <Elements stripe={stripePromise}>
            <PaymentForm {...props} />
        </Elements>
    );
} 